// useDeviceToken.js
import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useMutation } from "@apollo/client";
import useIsMountedRef from "../hooks/useIsMountedRef";
import { setToken, getToken } from "../actions/config";
import { DEVICE_TOKEN } from "./mutations/auth";
import { appUID as makeAppUID } from "../utils/others";
import handleGraphqlError from "./handleGraphqlError";
import { setAppUID } from "reducers/reducerIdentity";
import { getToken as getUserToken } from "../actions/user";

const useDeviceToken = (deviceLang, force = false) => {
    const dispatch = useDispatch();
    const mounted = useIsMountedRef();

    const appToken = useSelector(getToken);
    const userToken = useSelector(getUserToken);
    const token = userToken ? userToken : appToken;

    const uid = useSelector((s) => s.identity?.uid);
    const appUid = useSelector((s) => s.identity?.appUID);

    const appRef = useSelector((s) => s.appRef);
    const chainRef = useSelector((s) => s.chainRef);

    const regenOnceRef = useRef(false);

    useEffect(() => {
        if (!force && regenOnceRef.current) regenOnceRef.current = false;
    }, [force]);

    useEffect(() => {
        const needInitial = !appUid;
        const needForce = force && !regenOnceRef.current;

        if (needInitial || needForce) {
            const newUid = makeAppUID();
            console.log("Generated new appUID:", newUid);
            dispatch(setAppUID(newUid));
            if (needForce) regenOnceRef.current = true;
        }
    }, [appUid, force, dispatch]);

    const [deviceToken, { loading, error }] = useMutation(DEVICE_TOKEN.query, {
        context: { timeout: 10000 },
        onCompleted: (data) => {
            if (data?.deviceToken?.token) {
                dispatch(setToken(data.deviceToken.token));
            }
        },
        onError: (err) => {
            handleGraphqlError(err, dispatch, appToken, userToken);
        },
        fetchPolicy: "no-cache",
    });

    useEffect(() => {
        if ((!token || force) && appRef && appUid && uid) {
            deviceToken({
                variables: {
                    app: appUid,
                    device: uid,
                    language: deviceLang,
                    domain: appRef,
                    chainRef,
                },
            });
        }
    }, [deviceToken, uid, token, appRef, deviceLang, chainRef, appUid, force]);

    if (token) return [false, null, token];
    if (!mounted.current || !appRef || !appUid) return [true, null, token];
    return [loading, error, token];
};

export default useDeviceToken;
